import { useBunja } from 'bunja/react';
import { useAtomValue } from 'jotai';
import Image from 'next/image';

import { useRouter } from '@flipster/i18n/routing';
import { css, cx } from '@flipster/styled-system/css';
import { center, flex } from '@flipster/styled-system/patterns';
import { Price } from '@flipster/ui/Price';
import firstCrown from '@flipster/ui/assets/images/competition/first-crown.png';
import secondCrown from '@flipster/ui/assets/images/competition/second-crown.png';
import thirdCrown from '@flipster/ui/assets/images/competition/third-crown.png';
import worstCrown from '@flipster/ui/assets/images/competition/worst-crown.png';
import AwardRankerBgIcon from '@flipster/ui/assets/svgs/competition/award-ranker-bg.svg';
import AwardWorstBgIcon from '@flipster/ui/assets/svgs/competition/award-worst-bg.svg';
import { UsdPrecision, UsdtCurrency } from '@flipster/utils/constants/currency';
import webviewHandler from '@flipster/utils/webview-handler';

import { competitionBunja, competitionRankingBunja } from '../../bunja';

const awardMapper = [
  { text: '1st', image: firstCrown },
  { text: '2nd', image: secondCrown },
  { text: '3rd', image: thirdCrown },
  { text: 'Biggest loser', image: worstCrown },
];

export const WinnerAndLoser = () => {
  const router = useRouter();
  const { competitionAtom } = useBunja(competitionBunja);
  const { rankingsAtom, lastRankingAtom } = useBunja(competitionRankingBunja);
  const competition = useAtomValue(competitionAtom);
  const rankings = useAtomValue(rankingsAtom);
  const lastRanking = useAtomValue(lastRankingAtom);
  const awardList = [...rankings.slice(0, 3), lastRanking];
  const isPnlCompetition = competition?.rankBy === 'PNL';
  return (
    <ul
      className={flex({
        tabletDown: { flexDir: 'column', mb: 36, gap: 16 },
        tablet: { justifyContent: 'center', mb: 70, gap: 32 },
      })}
    >
      {awardMapper.map((award, i) => {
        const awardUser = awardList[i];
        if (!isPnlCompetition && i === awardMapper.length - 1) return null;
        return (
          <li
            className={center({
              flexDir: 'column',
              flex: 1,
              color: 'content.default.level-5',
              tablet: {
                maxW: 276,
              },
              '&:nth-child(4)': {
                '& .info': {
                  bgImage:
                    'linear-gradient(to bottom, {colors.background.default.level-3}, {colors.background.default.level-3}), linear-gradient(108deg, {colors.background.accent.sell}, 1%, {colors.background.accent.sell})',
                  boxShadow: '0 5px 41px 0 {colors.background.accent.sell/35}',
                },
              },
            })}
            key={`award-${award.text}`}
          >
            <Image
              src={award.image}
              width={58}
              height={37}
              alt={award.text}
              className={css({
                tabletDown: {
                  w: 36,
                  h: 23,
                  mb: 8,
                },
                tablet: {
                  w: 58,
                  h: 37,
                  mb: 16,
                },
              })}
            />
            <div
              className={cx(
                'info',
                css({
                  pos: 'relative',
                  w: '100%',
                  cursor: 'pointer',
                  bgColor: 'background.default.level-3',
                  bgImage:
                    'linear-gradient(to bottom, {colors.background.default.level-3}, {colors.background.default.level-3}), linear-gradient(108deg, #6ca2e8, 1%, {colors.background.accent.primary})',
                  backgroundClip: 'content-box, border-box',
                  backgroundOrigin: 'border-box',
                  borderWidth: '1px',
                  borderStyle: 'solid',
                  borderColor: 'transparent',
                  rounded: 16,
                  boxShadow: '0 5px 41px 0 {colors.background.accent.primary/35}',
                  _hover: {
                    _after: {
                      pos: 'absolute',
                      top: 0,
                      left: 0,
                      w: '100%',
                      h: '100%',
                      content: "''",
                      bgColor: 'inverse.white/10',
                      rounded: 8,
                    },
                  },
                }),
              )}
              onClick={() => {
                if (!awardUser) return;
                webviewHandler.postMessage(
                  {
                    action: 'link',
                    params: {
                      page: `user/profile/${awardUser.userProfileId}`,
                    },
                  },
                  () => router.push(`/user/profile/${awardUser.userProfileId}`),
                );
              }}
            >
              <div
                className={css({
                  pos: 'relative',
                  tabletDown: { h: 20, mb: 8 },
                  tablet: { h: 24, mb: 12 },
                  '& svg': {
                    position: 'absolute',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    tabletDown: { w: 150, h: 20 },
                    tablet: { w: 180, h: 24 },
                  },
                })}
              >
                {i < 3 ? <AwardRankerBgIcon /> : <AwardWorstBgIcon />}
                <p className={css({ pos: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)' })}>
                  {award.text}
                </p>
              </div>
              <p className={css({ textStyle: 'Title/S', textAlign: 'center', pb: { base: 6, tablet: 12 } })}>
                {awardUser?.nickname}
              </p>
              <div className={css({ textAlign: 'center', pb: { base: 20, tablet: 30 } })}>
                <Price
                  price={isPnlCompetition ? awardUser?.pnl : awardUser?.volume}
                  currency={UsdtCurrency}
                  precision={UsdPrecision}
                  withPlus={isPnlCompetition}
                  withColor
                  fontType='num1'
                />
              </div>
            </div>
          </li>
        );
      })}
    </ul>
  );
};
